/**
 * PostHog Event Emitter Utility
 * Used for China/global web and app WebView (global PostHog project for app).
 */
import isNonNumericEmail from "helpers/isNonNumericEmail";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { isNewNativeAppShell, parseAppShell } from "constants/appShell";
import { getEngageAttributionProps } from "./engageAttribution";
import {
  getBehaviouralPersonProperties,
  getPostHogConfig,
  getPostHogEventContext,
  isPostHogEnabled,
  setLastAppliedFilterForPostHog,
  touchLastActiveForPostHog,
  clearAccountScopedPostHogProperties,
} from "./posthogConfig";

export type PostHogIdentifyFunnel = "login" | "lead" | "book_form" | "payment";

let posthogInstance: any = null;

/**
 * The user id for this session, once we have one.
 *
 * In memory on purpose: it must survive across identify calls but not across a
 * logout. `posthogReset` starts a fresh anonymous person, and a stale id would
 * attach to whoever signs in next. A module variable dies with the page;
 * localStorage would not.
 */
let knownUserId: string | null = null;

export const setPostHogInstance = (instance: any) => {
  posthogInstance = instance;
};

export const getPostHogInstance = async (): Promise<any> => {
  if (!isPostHogEnabled()) {
    return null;
  }

  if (posthogInstance) {
    return posthogInstance;
  }

  try {
    // eslint-disable-next-line import/no-extraneous-dependencies
    const posthogModule = await import("posthog-js");
    return posthogModule.default;
  } catch {
    return null;
  }
};

interface PostHogEventParams {
  action: string;
  category?: string;
  label?: string;
  value?: string | number;
  /**
   * Applied after the super properties, so these win over them (e.g. an event-specific `source`).
   * Opt-in: every other prop is still overwritten by a super property of the same name.
   */
  overrides?: Record<string, unknown>;
  [key: string]: any;
}

/** Super properties applied to every capture (manual + autocapture). */
export function getPostHogSuperProperties(): Record<string, string | number | boolean> {
  const { source } = getPostHogConfig();
  return {
    ...getEngageAttributionProps(),
    ...getPostHogEventContext(),
    source,
  };
}

/** Re-register when Engage URL params / cookie change so autocapture stays attributed. */
export async function registerPostHogSuperProperties(): Promise<void> {
  if (!isPostHogEnabled()) {
    return;
  }

  try {
    // Every caller of this is a moment of activity — a route change, a filter
    // being applied, attribution params arriving — so the stamp rides the flush
    // that is already happening rather than costing a write of its own.
    touchLastActiveForPostHog();

    const ph = await getPostHogInstance();
    if (ph?.register) {
      const superProperties = getPostHogSuperProperties();
      ph.register(superProperties);
      if (ph.setPersonProperties) {
        const personProperties: Record<string, string | number | boolean> = {};
        if (superProperties.user_source_country) {
          personProperties.user_source_country = superProperties.user_source_country;
        }
        if (superProperties.user_destination_country) {
          personProperties.user_destination_country = superProperties.user_destination_country;
        }
        // Behavioural state lives only on the person, so it is not read off
        // `superProperties` — those all ride every event as well.
        Object.assign(personProperties, getBehaviouralPersonProperties());
        if (Object.keys(personProperties).length > 0) {
          ph.setPersonProperties(personProperties);
        }
      }
    }
  } catch (error) {
    console.error("PostHog super property registration failed:", error);
  }
}

/**
 * Put the user id on the PostHog person.
 *
 * Written from the profile effect, so every logged-in session carries it —
 * `loginSuccess` fires once, so a session established weeks ago never had it.
 *
 * Not persisted to localStorage, unlike the behavioural properties: see
 * `knownUserId` above.
 */
export const setUserIdForPostHog = async (userId?: string | number | null): Promise<void> => {
  if (!isPostHogEnabled()) return;
  const id = String(userId ?? "").trim();
  if (!id) return;

  try {
    knownUserId = id;
    const ph = await getPostHogInstance();
    if (ph?.setPersonProperties) {
      ph.setPersonProperties({ user_id: id });
    }
  } catch (error) {
    console.error("PostHog user id attribution failed:", error);
  }
};

export const posthogEventEmitter = async ({
  action,
  category = "",
  label = "",
  value = "",
  overrides,
  ...additionalProps
}: PostHogEventParams): Promise<void> => {
  if (!isPostHogEnabled() || !action) {
    return;
  }

  // Snapshot before any await — card/link clicks navigate immediately while this
  // function is still waiting on the PostHog client, which would otherwise stamp
  // the destination URL on a homepage interaction.
  const pageUrlAtEmit = typeof window !== "undefined" ? window.location.href : "";
  const pagePathAtEmit = typeof window !== "undefined" ? window.location.pathname : "";

  try {
    const ph = await getPostHogInstance();
    if (!ph) {
      return;
    }

    // Every search filter funnels through here — `amenities_filter_applied`,
    // `stay_duration_filter_applied`, and six more. Recording the name once, at
    // the chokepoint, keeps `last_applied_filter` correct without eight call
    // sites that can drift apart.
    //
    // Flushed immediately rather than left for the next route change: applying
    // a filter only changes the query string, and the route effect that pushes
    // person properties is keyed on `location.pathname`, so it would not fire.
    if (/(^|_)filter_applied$/.test(action)) {
      setLastAppliedFilterForPostHog(action);
      registerPostHogSuperProperties().catch(() => undefined);
    }

    // Activity is not only navigation. An app WebView session can sit on one
    // screen firing events without ever changing route, and those people were
    // the bulk of the gap between PostHog's `last_seen_at` and reality. The
    // throttle inside `touchLastActiveForPostHog` means this flushes at most
    // once every 15 minutes no matter how many events pass through here.
    if (touchLastActiveForPostHog()) {
      registerPostHogSuperProperties().catch(() => undefined);
    }

    const eventProperties = {
      ...(category && { event_category: category }),
      ...(label && { event_label: label }),
      ...(value !== "" && value !== undefined && { value }),
      ...additionalProps,
      ...getPostHogSuperProperties(),
      ...(pageUrlAtEmit && {
        page_url: pageUrlAtEmit,
        page_path: pagePathAtEmit,
        $current_url: pageUrlAtEmit,
      }),
      ...overrides,
    };

    ph.capture(action, eventProperties);
  } catch (error) {
    console.error("PostHog event emission failed:", error);
  }
};

/** Identify user in PostHog using email as distinct id (skips invalid / numeric-only local parts). */
export const identifyPostHogUserByEmail = async (
  email: string | undefined | null,
  properties?: Record<string, any>,
): Promise<void> => {
  const normalizedEmail = typeof email === "string" ? email.trim() : "";
  if (!normalizedEmail || !isNonNumericEmail(normalizedEmail)) {
    return;
  }

  await posthogIdentify(normalizedEmail, {
    email: normalizedEmail,
    ...properties,
  });
};

export const posthogIdentify = async (
  userId: string,
  userProperties?: Record<string, any>,
): Promise<void> => {
  if (!isPostHogEnabled()) {
    return;
  }

  try {
    const ph = await getPostHogInstance();
    if (!ph) {
      return;
    }

    ph.identify(userId, {
      ...getEngageAttributionProps(),
      ...getPostHogEventContext(),
      // Every identify carries it once known, not just the one that learned it.
      ...(knownUserId ? { user_id: knownUserId } : {}),
      ...userProperties,
    });
  } catch (error) {
    console.error("PostHog identify failed:", error);
  }
};

export const posthogReset = async (): Promise<void> => {
  // Before the early return: the user is gone either way, and carrying their id
  // or shortlist into the next session is the one thing that must not happen.
  knownUserId = null;
  clearAccountScopedPostHogProperties();

  if (!isPostHogEnabled()) {
    return;
  }

  try {
    const ph = await getPostHogInstance();
    if (!ph) {
      return;
    }

    ph.reset();
    // Send new anonymous distinct_id to native for profile continuity
    if (
      typeof window !== "undefined" &&
      isNewNativeAppShell(parseAppShell((window as any).appShell))
    ) {
      const newDistinctId = ph.get_distinct_id();
      if (newDistinctId) {
        sendWebViewEvent(WEB_VIEW_APP_EVENTS.POSTHOG_DISTINCT_ID, {
          distinctId: newDistinctId,
        });
      }
    }
  } catch (error) {
    console.error("PostHog reset failed:", error);
  }
};

export default posthogEventEmitter;
